import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ReportsPage from './pages/ReportsPage';
import EmployeesPage from './pages/EmployeesPage';
import SitesPage from './pages/SitesPage';
import SettingsPage from './pages/SettingsPage';
import ApprovalPage from './pages/ApprovalPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import EmailMonitorPage from './pages/EmailMonitorPage';
import PDFSettingsPage from './pages/PDFSettingsPage';
import './App.css';

// Registrazione Service Worker per PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('Service Worker registrato con successo:', registration.scope);
      })
      .catch((error) => {
        console.log('Registrazione Service Worker fallita:', error);
      });
  });
}

const ProtectedRoute = ({ children, adminOnly = false, adminOrGestore = false }) => {
  const { user, loading, isAdmin, isGestore } = useAuth();
  const location = useLocation();

  // Save intended path to sessionStorage immediately on mount if not authenticated
  useEffect(() => {
    if (!loading && !user && location.pathname !== '/login' && location.pathname !== '/') {
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    }
  }, [loading, user, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B6B93]"></div>
      </div>
    );
  }

  if (!user) {
    // Save intended URL in sessionStorage for persistence across page refreshes
    if (location.pathname !== '/login' && location.pathname !== '/') {
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    }
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOrGestore && !isAdmin() && !isGestore()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B6B93]"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes - no auth required */}
      <Route path="/approve/:token" element={<ApprovalPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      
      <Route 
        path="/login" 
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute adminOrGestore>
            <EmployeesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sites"
        element={
          <ProtectedRoute adminOrGestore>
            <SitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute adminOnly>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-monitor"
        element={
          <ProtectedRoute adminOnly>
            <EmailMonitorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pdf-settings"
        element={
          <ProtectedRoute adminOnly>
            <PDFSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
